const React = require('react') // NOTE: import required even though not explicitly used used in this file
const Sentry = require('@sentry/react')
const {
	SENTRY_FILTER_COOKIES,
	SENTRY_FILTER_REQUEST_HEADERS,
	SENTRY_FILTER_RESPONSE_HEADERS,
	filterKeyValueMap,
	isCanceledSentryEvent,
	scrubQueryString,
	scrubUrlQueryParams
} = require('./sentryScrubHelpers')

const getSentryAllowUrls = (assetsPath) => {
	if (!assetsPath) {
		return [/webpack-internal:\/\//, /^https?:\/\/localhost(?::\d+)?\//]
	}
	const normalized = assetsPath.endsWith('/') ? assetsPath : `${assetsPath}/`
	const escaped = normalized.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
	return [new RegExp(`^${escaped}`), /webpack-internal:\/\//, /^https?:\/\/localhost(?::\d+)?\//]
}

module.exports = () => ({
	enhanceApp: ({ DefaultApp, state }) => {
		const { rootWebViewProps } = state
		const { appVersion, assetsPath, sentryOptions } = rootWebViewProps

		// init sentry for client side
		Sentry.init({
			release: appVersion,
			enabled: sentryOptions.isEnabled,
			dsn: sentryOptions.dsn,
			allowUrls: getSentryAllowUrls(assetsPath),
			enableLogs: true,
			/**
			 * Browser events: page URL is on `event.request`; XHR breadcrumbs use `crumb.data.url`.
			 * @param {import('@sentry/react').ErrorEvent} event
			 */
			beforeSend: (event) => {
				if (isCanceledSentryEvent(event)) {
					return null
				}
				if (event.request?.url) {
					event.request.url = scrubUrlQueryParams(event.request.url)
				}
				if (event.request?.query_string) {
					event.request.query_string = scrubQueryString(event.request.query_string)
				}
				event.breadcrumbs?.forEach((crumb) => {
					if (crumb.data?.url) {
						crumb.data.url = scrubUrlQueryParams(crumb.data.url)
					}
					if (crumb.data?.['http.query']) {
						const qs = scrubQueryString(crumb.data['http.query'])
						crumb.data['http.query'] = qs ? `?${qs}` : crumb.data['http.query']
					}
				})
				filterKeyValueMap(event.request?.cookies, SENTRY_FILTER_COOKIES)
				filterKeyValueMap(event.request?.headers, SENTRY_FILTER_REQUEST_HEADERS)
				filterKeyValueMap(event.response?.headers, SENTRY_FILTER_RESPONSE_HEADERS)
				return event
			},
			/**
			 * @param {import('@sentry/react').TransactionEvent} transaction
			 */
			beforeSendTransaction: (transaction) => {
				if (transaction.request?.url) {
					transaction.request.url = scrubUrlQueryParams(transaction.request.url)
				}
				if (transaction.request?.query_string) {
					transaction.request.query_string = scrubQueryString(transaction.request.query_string)
				}
				transaction.breadcrumbs?.forEach((crumb) => {
					if (crumb.data?.url) {
						crumb.data.url = scrubUrlQueryParams(crumb.data.url)
					}
					if (crumb.data?.['http.query']) {
						const qs = scrubQueryString(crumb.data['http.query'])
						crumb.data['http.query'] = qs ? `?${qs}` : crumb.data['http.query']
					}
				})
				filterKeyValueMap(transaction.request?.cookies, SENTRY_FILTER_COOKIES)
				filterKeyValueMap(transaction.request?.headers, SENTRY_FILTER_REQUEST_HEADERS)
				filterKeyValueMap(transaction.response?.headers, SENTRY_FILTER_RESPONSE_HEADERS)
				return transaction
			},
			beforeSendLog: (log) => {
				/*
				 * React warnings are interpreted via `console.error`
				 * `CONSOLE` prefix is used to avoid duplicate logging in Sentry
				 */
				if (log.message.startsWith('Warning:') || log.message.startsWith('CONSOLE')) {
					return null
				}

				if (log.attributes) {
					log.attributes.runtime = 'browser'
				}

				return log
			},
			integrations: [
				Sentry.browserTracingIntegration(),
				Sentry.consoleLoggingIntegration({
					levels: ['error']
				})
			],
			tracesSampleRate: 0.05,
			environment: sentryOptions.environment,
			sendDefaultPii: false
		})

		return (props) => <DefaultApp {...props} />
	}
})
